import { History } from 'history';
import { getLocalStorage, setLocalStorage, removeLocalStorage } from '@/utils/localStorage';
import { REPORTED_KEY, PROMO_GAME_CHANNEL, PROMO_LANDING_GAME_ID_KEY, PROMO_LANDING_JUMPED_KEY } from './types';

export type PromoLandingSplit = {
  /** 有 #gameId#gamech 落地段时的游戏 id，否则 null */
  gameId: number | null;
  /** 去掉落地段后的买量串，供来源判断 / 注册上报；无落地段则为原串 */
  reportHisId: string;
  /** 是否匹配到 #gameId#gamech 落地段 */
  hasLandingMarker: boolean;
};

/** 是否为纯数字 game_id */
export function isPromoLandingGameIdSegment(segment: string): boolean {
  return /^\d+$/.test(String(segment || '').trim());
}

/**
 * 拆分 SEOINFORMATION（落地段放在后面，避免影响外壳 [0] 上报识别）：
 * - `原买量…#1392#gamech#1` → gameId=1392，reportHisId=原买量…#1
 * - `原买量…#1392#gamech` → gameId=1392，reportHisId=原买量…
 * - 兼容旧前缀 `1392#gamech#原买量…`
 * - 无落地段 → gameId=null，reportHisId=原串
 */
export function splitPromoLandingPrefix(hisId: string): PromoLandingSplit {
  const trimmed = String(hisId || '').trim();
  if (!trimmed) {
    return { gameId: null, reportHisId: '', hasLandingMarker: false };
  }
  const paramArr = trimmed.split('#');

  // 优先：后缀/中部 …#gameId#gamech（#gamech 紧跟在纯数字 gameId 后）
  for (let i = 1; i < paramArr.length; i += 1) {
    if ((paramArr[i] || '').trim() !== PROMO_GAME_CHANNEL) continue;
    const gameIdRaw = (paramArr[i - 1] || '').trim();
    if (!isPromoLandingGameIdSegment(gameIdRaw)) continue;
    const gameIdNum = Number(gameIdRaw);
    const reportParts = paramArr.slice(0, i - 1).concat(paramArr.slice(i + 1));
    return {
      gameId: gameIdNum > 0 ? gameIdNum : null,
      reportHisId: reportParts.join('#'),
      hasLandingMarker: true
    };
  }

  // 兼容旧前缀：gameId#gamech#原买量…
  const gameIdRaw0 = (paramArr[0] || '').trim();
  const channel1 = (paramArr[1] || '').trim();
  if (isPromoLandingGameIdSegment(gameIdRaw0) && channel1 === PROMO_GAME_CHANNEL) {
    const gameIdNum = Number(gameIdRaw0);
    return {
      gameId: gameIdNum > 0 ? gameIdNum : null,
      reportHisId: paramArr.slice(2).join('#'),
      hasLandingMarker: true
    };
  }

  return { gameId: null, reportHisId: trimmed, hasLandingMarker: false };
}

/** 仅跳转占位剩余串（如 1、1#1、1#1#1#1），不应当作买量来源 */
export function isTrivialPromoReportHisId(reportHisId: string): boolean {
  const t = String(reportHisId || '').trim();
  if (!t) return true;
  return /^1(#1)*$/.test(t);
}

/**
 * 登录页打开时规范化 SEOINFORMATION：
 * - 有 `#gameId#gamech`：重置 promoLandingGameId / promoLandingJumped，写入新 gameId
 *   - 仅跳转（无有效买量串）→ 清空 SEOINFORMATION
 *   - 上报+跳转 → 去掉落地段后的买量参数写回 SEOINFORMATION
 * - 无落地段：不改动
 * @returns 规范化后的 SEOINFORMATION 值（供来源判断 / hisId）
 */
export function normalizePromoLandingSeoOnLogin(shellHisId?: string): string {
  const raw =
    shellHisId !== undefined && shellHisId !== null
      ? String(shellHisId).trim()
      : (getLocalStorage(REPORTED_KEY) || '').trim();
  if (!raw) {
    return '';
  }
  const { gameId, reportHisId, hasLandingMarker } = splitPromoLandingPrefix(raw);
  if (!hasLandingMarker) {
    return raw;
  }
  // 新携带 gamech：覆盖 gameId，并清除已跳转标记，允许本次再跳
  if (gameId) {
    setLocalStorage(PROMO_LANDING_GAME_ID_KEY, String(gameId));
  } else {
    removeLocalStorage(PROMO_LANDING_GAME_ID_KEY);
  }
  removeLocalStorage(PROMO_LANDING_JUMPED_KEY);
  if (isTrivialPromoReportHisId(reportHisId)) {
    removeLocalStorage(REPORTED_KEY);
    return '';
  }
  setLocalStorage(REPORTED_KEY, reportHisId);
  return reportHisId;
}

/**
 * 取上报/来源判断用 hisId。
 * 登录页规范化后 SEOINFORMATION 已是旧格式；此处仍兼容未截断的带落地段串。
 */
export function getReportHisIdFromSeo(hisIdHint?: string): string {
  const raw = hisIdHint !== undefined && hisIdHint !== null ? String(hisIdHint) : getLocalStorage(REPORTED_KEY) || '';
  const { reportHisId, hasLandingMarker } = splitPromoLandingPrefix(raw);
  if (hasLandingMarker && isTrivialPromoReportHisId(reportHisId)) {
    return '';
  }
  return reportHisId;
}

/**
 * 主窗就绪后消费一次：用 PROMO_LANDING_GAME_ID_KEY 跳转游戏详情。
 * 不改动 SEOINFORMATION（登录页已截断写回）；用 PROMO_LANDING_JUMPED_KEY 防重复跳。
 */
export function tryConsumePromoLandingJump(history: History): boolean {
  if (getLocalStorage(PROMO_LANDING_JUMPED_KEY) === '1') {
    return false;
  }
  const gameIdRaw = (getLocalStorage(PROMO_LANDING_GAME_ID_KEY) || '').trim();
  if (!isPromoLandingGameIdSegment(gameIdRaw)) {
    return false;
  }
  const gameId = Number(gameIdRaw);
  if (!gameId || gameId <= 0) {
    return false;
  }
  history.push('/gameDetail', {
    id: gameId,
    type: 1,
    islanding: true
  });
  setLocalStorage(PROMO_LANDING_JUMPED_KEY, '1');
  return true;
}
